import { useState, type FormEvent } from 'react';
import '../styles/newsletter.css';
import { FaCheckCircle, FaEnvelopeOpenText, FaPaperPlane } from 'react-icons/fa';
import { getEnv } from '../utils/env';

const newsletterEndpoint = getEnv('NEWSLETTER_MAIL_URL', '/api/mail/newsletter');

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STATUS_HINT = 'Join for concise updates, surveys, and offers.';
const MAX_STATUS_LENGTH = 280;

function sanitizeStatusMessage(raw: string): string {
  const text = String(raw || '')
    .replace(/<[^>]*>/g, '')
    .trim()
    .slice(0, MAX_STATUS_LENGTH);
  return text || 'Thank you — you are subscribed.';
}

export default function Newsletters() {
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = email.trim();

    if (!emailPattern.test(trimmed)) {
      setSubmitError('Please enter a valid email address.');
      setEmailStatus('');
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    setEmailStatus('');

    const params = new URLSearchParams({ sendto: trimmed });
    const xhr = new XMLHttpRequest();

    xhr.addEventListener('load', () => {
      const ok = xhr.status >= 200 && xhr.status < 400;
      setSubmitting(false);
      if (ok) {
        setEmailStatus(
          sanitizeStatusMessage(xhr.responseText) ||
            'Thank you — you are subscribed.',
        );
        setEmail('');
      } else {
        setSubmitError(
          'We could not subscribe you right now. Please try again later.',
        );
      }
    });

    xhr.addEventListener('error', () => {
      setSubmitting(false);
      setSubmitError(
        'Network error. Please check your connection and try again.',
      );
    });

    xhr.open('GET', `${newsletterEndpoint}?${params.toString()}`);
    xhr.send();
  };

  return (
    <section className="newsletter-section home-surface--light" aria-labelledby="newsletter-heading">
      <div className="container">
        <div className="newsletter-section__shell subscribe">
          <div className="newsletter-section__visual" aria-hidden="true">
            <span className="newsletter-section__icon">
              <FaEnvelopeOpenText />
            </span>
            <div className="newsletter-section__signal newsletter-section__signal--top">
              <strong>Delivery notes</strong>
              <span>Web, ecommerce, AI</span>
            </div>
            <div className="newsletter-section__signal newsletter-section__signal--bottom">
              <strong>Practical ideas</strong>
              <span>No inbox noise</span>
            </div>
          </div>
          <div className="newsletter-section__content">
            <p className="newsletter-section__eyebrow">Email newsletter</p>
            <h2 id="newsletter-heading" className="subscribe-title newsletter-section__title">
              Useful digital delivery insights, straight to your inbox.
            </h2>
            <p className="newsletter-section__copy">
              Get occasional Plux Infosystems updates on web development, ecommerce optimization,
              automation, performance, and product delivery.
            </p>
            <ul className="newsletter-section__benefits" aria-label="Newsletter benefits">
              <li><FaCheckCircle aria-hidden="true" /> Practical guides</li>
              <li><FaCheckCircle aria-hidden="true" /> Service updates</li>
              <li><FaCheckCircle aria-hidden="true" /> No spam</li>
            </ul>
            <form className="subscribe-form newsletter-section__form" method="GET" onSubmit={submitForm}>
              <label className="sr-only" htmlFor="newsletter-email">Email address</label>
              <input
                id="newsletter-email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setSubmitError('');
                }}
                className="email input-standard-grey input-white newsletter-section__input"
                placeholder="Your email address"
                type="email"
                autoComplete="email"
                required
                disabled={submitting}
              />
              <button
                type="submit"
                className="subscr-btn newsletter-section__button"
                disabled={submitting}
              >
                {submitting ? 'Subscribing…' : 'Subscribe'} <FaPaperPlane aria-hidden="true" />
              </button>
            </form>
            <div className="newsletter-section__status" role="status" aria-live="polite">
              {submitError || emailStatus || STATUS_HINT}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
